/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useAsyncEffect } from 'src/core/hooks/useAsyncEffect';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { tryParseJson } from 'src/core/utils/base';
import { getEventBus } from 'src/core/utils/eventBus';
import { getStorage } from 'src/core/utils/getStorage';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { getLuisAppService } from 'src/luis/core/services/apps/luisAppService';
import { EntityMigrationNotificationCard } from 'src/luis/modules/entities/hooks/useCompositeEntityMigration/EntityMigrationNotificationCard';

const eventBus = getEventBus();
const messages = defineMessages({
    title: {
        id: 'entityMigration.title',
        defaultMessage: 'Upgrades available'
    },
    failedTrainingToastTitle: {
        id: 'entityMigration.failedTrainingToastTitle',
        defaultMessage: 'Training failed'
    },
    failedTrainingToastContent: {
        id: 'entityMigration.failedTrainingToastContent',
        defaultMessage: 'We were unable to train version { upgradedVersionId }. Please try again later.'
    },
    failedMigrationToastTitle: {
        id: 'entityMigration.failedMigrationToastTitle',
        defaultMessage: 'Upgrading composites entities has failed'
    },
    failedMigrationToastContent: {
        id: 'entityMigration.failedMigrationToastContent',
        defaultMessage: 'Upgrading your composite entities has failed. Please try again later.'
    }
});

/**
 * Creates a hook that returns functions that can manage
 * showing/hiding of the entity migration card.
 */
export const useCompositeEntityMigration = () => {
    const { localize } = useLocalization();
    const { dispatcher, rootStore } = useGlobalState();
    const { serviceKey } = rootStore;
    const { getAppsToUpgrade } = getLuisAppService();

    const [appsToUpgrade, setAppsToUpgrade] = React.useState([]);
    const [isCompositeEntityMigrationNeeded, setIsCompositeEntityMigrationNeeded] = React.useState(false);

    useAsyncEffect(async () => {
        const storageData = getStorage('sessionStorage').getData(LuisConstants.StorageKeys.ConfirmSuccessfulMigrationStorageKey);
        const isVisible = tryParseJson<boolean>(storageData) ?? false;

        dispatcher.dispatch('updateCompositeEntitiesMigrationCoachMarkVisibility', { isVisible });

        if (serviceKey.value) {
            setAppsToUpgrade(await getAppsToUpgrade());
        }
    }, [serviceKey.value]);

    const doesMigrationToastExist = !!rootStore.toastStoreHouse.findById(LuisConstants.MigrationNotificationToastId);

    React.useEffect(() => {
        // Get the previous date of dismissal if exists.
        const lastDate = new Date(getStorage('localStorage').getData(LuisConstants.StorageKeys.LastDismissalMigrationDateStorageKey));
        lastDate.setDate(lastDate.getDate() + 7);
        const today = new Date();

        /*
         * If a week passed on the last dismissal date and still
         * there are apps that need migration then add toast to
         * the notification pane and show the message bar.
         */
        if (lastDate <= today && appsToUpgrade?.length) {
            setIsCompositeEntityMigrationNeeded(true);

            /**
             * The following checks if the notification is already
             * pushed in a previous time to prevent duplication.
             */
            if (!doesMigrationToastExist) {
                eventBus.emit('addToast', {
                    quiet: true,
                    status: 'informative',
                    title: localize(messages.title),
                    id: LuisConstants.MigrationNotificationToastId,
                    content: <EntityMigrationNotificationCard appsToUpgrade={appsToUpgrade} />
                });
            } else {
                eventBus.emit('updateToast', {
                    id: LuisConstants.MigrationNotificationToastId,
                    toast: {
                        quiet: true,
                        status: 'informative',
                        title: localize(messages.title),
                        content: <EntityMigrationNotificationCard appsToUpgrade={appsToUpgrade} />
                    }
                });
            }
        } else if (isCompositeEntityMigrationNeeded) {
            setIsCompositeEntityMigrationNeeded(false);
            if (doesMigrationToastExist) {
                dispatcher.dispatch('removeToast', { id: LuisConstants.MigrationNotificationToastId });
            }
        }
    }, [appsToUpgrade]);

    React.useEffect(() => {
        if (isCompositeEntityMigrationNeeded && !doesMigrationToastExist) {
            dismissEntityMigration();
        }
    }, [rootStore.toastStoreHouse.items.length]);

    /**
     * Dismiss the notifications for the migration for one week.
     * This includes the message bar and the notification toast.
     */
    const dismissEntityMigration = () => {
        setIsCompositeEntityMigrationNeeded(false);
        dispatcher.dispatch('dismissMigration');
    };

    /**
     * Adds a toast to notify user with migration failure.
     */
    const addMigrationFailureToast = () => {
        eventBus.emit('addToast', {
            quiet: true,
            status: 'failed',
            title: localize(messages.failedMigrationToastTitle),
            content: localize(messages.failedMigrationToastContent)
        });
    };

    /**
     * Adds a toast to notify user with upgraded version training failure.
     */
    const addTrainingFailureToast = (upgradedVersionId: string) => {
        eventBus.emit('addToast', {
            quiet: true,
            status: 'failed',
            title: localize(messages.failedTrainingToastTitle),
            content: localize(messages.failedTrainingToastContent, { upgradedVersionId })
        });
    };

    return {
        appsToUpgrade,
        dismissEntityMigration,
        addTrainingFailureToast,
        addMigrationFailureToast,
        isCompositeEntityMigrationNeeded
    };
};
